import { TECHNICAL_SECTIONS, FINANCIAL_SECTIONS } from "./export.js";

export type SectionStatus = "ready" | "weak" | "empty";

export interface SectionReadiness {
  key: string;
  arLabel: string;
  enLabel: string;
  status: SectionStatus;
  reason: string;     // legacy English message (kept for back-compat)
  arMessage: string;  // localized AR message
  enMessage: string;  // localized EN message
  wordCount: number;
  type: "technical" | "financial";
}

export interface ReadinessReport {
  ready: boolean;
  technical: SectionReadiness[];
  financial: SectionReadiness[];
  summary: {
    readyCount: number;
    weakCount: number;
    emptyCount: number;
    totalSections: number;
  };
}

const MIN_WORDS: Record<string, number> = {
  introduction: 120,
  scopeUnderstanding: 200,
  methodology: 250,
  team: 150,
  timeline: 80,
  qualityManagement: 150,
  riskManagement: 100,
  conclusion: 80,
  financialSummary: 100,
  pricingTable: 30,
  paymentTerms: 80,
  financialGuarantees: 60,
  totalAmount: 5,
};

// Common placeholder / TODO patterns that the AI sometimes leaves behind
// (English + Arabic). If any matches, the section is downgraded.
const PLACEHOLDER_PATTERNS: Array<{ re: RegExp; en: string; ar: string }> = [
  { re: /\b(TBD|TODO|FIXME|XXX|PLACEHOLDER|N\/A)\b/i, en: "Contains TBD/TODO placeholder", ar: "يحتوي على نص مؤقت (TBD/TODO)" },
  { re: /\[[^\]\n]{2,40}\]/, en: "Contains [bracket placeholder] text", ar: "يحتوي على نص بين أقواس مربعة [...]" },
  { re: /_{3,}/, en: "Contains blank fill-in (___)", ar: "يحتوي على فراغات للتعبئة (___)" },
  { re: /<[^>\n]{2,40}>/, en: "Contains <angle placeholder> text", ar: "يحتوي على نص بين أقواس <...>" },
  { re: /لا\s*يوجد\s*محتوى/, en: "Contains '(لا يوجد محتوى)' placeholder", ar: "يحتوي على عبارة (لا يوجد محتوى)" },
  { re: /اكتب\s*هنا|أدخل\s*هنا/, en: "Contains 'write here' placeholder", ar: "يحتوي على عبارة (اكتب هنا/أدخل هنا)" },
  { re: /\bLorem\s+ipsum\b/i, en: "Contains lorem-ipsum filler", ar: "يحتوي على نص افتراضي Lorem ipsum" },
];

function evaluate(
  key: string,
  content: string | null | undefined,
  type: "technical" | "financial",
  arLabel: string,
  enLabel: string,
): SectionReadiness {
  const text = (content ?? "").trim();
  const wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0;
  const min = MIN_WORDS[key] ?? 50;

  let status: SectionStatus = "ready";
  let enMessage = "";
  let arMessage = "";

  if (!text) {
    status = "empty";
    enMessage = "Section is empty";
    arMessage = "القسم فارغ";
  } else if (wordCount < min / 2) {
    status = "empty";
    enMessage = `Very short (${wordCount} words, recommended ${min}+)`;
    arMessage = `قصير جداً (${wordCount} كلمة، يُوصى بـ ${min}+)`;
  } else if (wordCount < min) {
    status = "weak";
    enMessage = `Short (${wordCount} words, recommended ${min}+)`;
    arMessage = `قصير (${wordCount} كلمة، يُوصى بـ ${min}+)`;
  } else {
    enMessage = `OK (${wordCount} words)`;
    arMessage = `جيد (${wordCount} كلمة)`;
  }

  // Placeholder detection — downgrades a "ready" section to "weak"
  if (text && status === "ready") {
    for (const p of PLACEHOLDER_PATTERNS) {
      if (p.re.test(text)) {
        status = "weak";
        enMessage = p.en;
        arMessage = p.ar;
        break;
      }
    }
  }

  // Financial-specific checks
  if (key === "pricingTable" && text) {
    const hasTable = text.includes("|");
    const hasNumbers = /\d/.test(text);
    if (!hasTable) {
      status = "weak";
      enMessage = "No table structure detected — pricing should be a markdown table";
      arMessage = "لم يتم اكتشاف بنية جدول — يجب أن تكون الأسعار جدولاً منظماً";
    } else if (!hasNumbers) {
      status = "weak";
      enMessage = "No numeric values in the pricing table";
      arMessage = "لا توجد قيم رقمية في جدول الأسعار";
    }
  }
  if (key === "totalAmount" && text) {
    const hasNumbers = /\d/.test(text);
    if (!hasNumbers) {
      status = "weak";
      enMessage = "No numeric total amount detected";
      arMessage = "لم يتم اكتشاف مبلغ إجمالي رقمي";
    }
  }

  return {
    key, arLabel, enLabel,
    status,
    reason: enMessage,
    arMessage, enMessage,
    wordCount, type,
  };
}

export function evaluateReadiness(draft: Record<string, any>): ReadinessReport {
  const technical = TECHNICAL_SECTIONS.map((s) =>
    evaluate(s.key, draft[s.key] ?? draft[camelToSnake(s.key)], "technical", s.arLabel, s.enLabel),
  );
  const financial = FINANCIAL_SECTIONS.map((s) =>
    evaluate(s.key, draft[s.key] ?? draft[camelToSnake(s.key)], "financial", s.arLabel, s.enLabel),
  );

  const all = [...technical, ...financial];
  const readyCount = all.filter((s) => s.status === "ready").length;
  const weakCount = all.filter((s) => s.status === "weak").length;
  const emptyCount = all.filter((s) => s.status === "empty").length;

  return {
    ready: emptyCount === 0,
    technical,
    financial,
    summary: { readyCount, weakCount, emptyCount, totalSections: all.length },
  };
}

function camelToSnake(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
}
